/**
 * ToolbarGroup: a collapsible inline group for the ViewerToolbar.
 *
 * When collapsed, shows a single icon button with an optional active-dot badge.
 * When expanded, reveals child toggles inline in the same toolbar strip.
 * Accordion behavior is managed by the parent (one open group at a time).
 */

import { type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface ToolbarGroupProps {
  icon: LucideIcon;
  label: string;
  isOpen: boolean;
  onToggle: () => void;
  /** When true, shows a small dot badge on the collapsed icon to indicate an active item */
  hasActiveItem: boolean;
  children: ReactNode;
}

export function ToolbarGroup({
  icon: Icon,
  label,
  isOpen,
  onToggle,
  hasActiveItem,
  children,
}: ToolbarGroupProps) {
  return (
    <div className="flex items-center gap-1">
      {/* Group icon button — always visible */}
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onToggle}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${label}`}
              aria-expanded={isOpen}
              className={`relative flex h-7 w-7 items-center justify-center rounded transition-all hover:opacity-100 ${
                isOpen
                  ? 'bg-foreground/10 opacity-100'
                  : 'opacity-50 hover:opacity-80'
              }`}
            >
              <Icon className="h-4 w-4 text-foreground" />
              {/* Active-item dot badge — shown when collapsed and something is active */}
              {!isOpen && hasActiveItem && (
                <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-sky-400" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="border-border bg-background text-foreground"
          >
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Expandable children — slide in/out with overflow clip + max-width transition */}
      <div
        className={`flex items-center gap-1 overflow-hidden transition-all duration-200 ease-in-out ${
          isOpen ? 'max-w-96 opacity-100' : 'max-w-0 opacity-0'
        }`}
        aria-hidden={!isOpen}
      >
        {/* Inner wrapper ensures the items don't wrap during animation */}
        <div className="flex items-center gap-1">{children}</div>

        {/* Subtle separator after the expanded group */}
        <div className="mx-0.5 h-4 w-px bg-foreground/20" />
      </div>
    </div>
  );
}
